import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { message } from "antd";
import { DateTime } from "luxon";
import GanttChart from "../../components/GanttChart";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStores } from "../../stores/setup/use-store";
import { StoreState } from "../../enum/StoreState";
import DatePicker from "react-datepicker";
import {
  AlertIcon,
  Box,
  Button,
  Center,
  Spinner,
  Text,
  Alert,
  Flex,
  Icon,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { CreateDashboardModal } from "../Event/components/CreateDashboardModal";
import { useTranslation } from "react-i18next";
import { getDateFnsLocale } from "../../utils/localeUtils";
import { PageTitle } from "../../components/PageTitle/PageTitle";
import TodayIcon from "@material-ui/icons/Today";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import axios from "axios";
import noDashboards from "../../assets/noDashboards.svg";
import dashboardHasNoLocation from "../../assets/dashboardHasNoLocation.svg";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import EmptyState from "../../components/EmptyState/EmptyState";
import useWindowObserver from "../../hooks/useWindowObserver";
import { useInsightNotificationWatcher } from "../../hooks/useInsightNotificationWatcher";
import { SCREEN_HORIZONTAL_SPACING } from "../../constants";
import Highcharts from "highcharts";
import DashboardSelect from "./components/DashboardSelect";
import { ChartBarStacked, Plus } from "lucide-react";
import { isEmbeddedMode, navigateParent } from "../../util/embedded";
import { rowStatusText } from "./rowStatusText";
import {
  ganttAxisAnchor,
  ganttXAxisRange,
  isGanttWithoutEvents,
} from "./ganttEmptyState";


const OceanWeatherIntelligence: React.FC = () => {
  const {
    dataStores: {
      operationRuleStore,
      oceanWeatherIntelligenceStore,
      operationRuleLocationInsightStore,
      operationRuleLocationStore,
      authStore,
      userConfigStore,
      notificationsStore,
    },
  } = useStores();
  const history = useHistory();

  const [clickedDate, setClickedDate] = useState(
    !!oceanWeatherIntelligenceStore.selectForecastDateAvailable,
  );
  const [startDate, setStartDate] = useState(
    oceanWeatherIntelligenceStore.processDateCurrent(DateTime.local()),
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const windowSize = useWindowObserver(".main-area");

  const handleInsightNotification = useCallback(() => {
    oceanWeatherIntelligenceStore.setChangeForUpdate({
      update: true,
      expiration: 0,
    });
    oceanWeatherIntelligenceStore.setActionCallGanttChart();
  }, [oceanWeatherIntelligenceStore]);

  useInsightNotificationWatcher({
    notifications: notificationsStore.notifications,
    onInsightNotificationReceived: handleInsightNotification,
  });

  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.oceanWeatherIntelligence",
  });

  // Translation for event module (CreateDashboardModal)
  const { t: tEvent } = useTranslation("translation", {
    keyPrefix: "event",
  });

  useEffect(() => {
    const date = oceanWeatherIntelligenceStore.processDateCurrent(
      DateTime.local(),
    );
    setStartDate(date);
  }, [userConfigStore.timezone]);

  let today = DateTime.local()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .setLocale("pt-BR")
      .setZone("America/Bahia")
      .toMillis();

  useEffect(() => {
    if (operationRuleStore.operationRules.length === 0) {
      operationRuleStore.getOperationRulesApi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();

    const operationRuleId = operationRuleStore.operationRuleMainId;

    if (operationRuleId <= 0) {
      return;
    }

    if (!oceanWeatherIntelligenceStore.needUpdateGanttChart()) {
      return;
    }

    const selectedDate =
      oceanWeatherIntelligenceStore.selectForecastDateAvailable
        ? oceanWeatherIntelligenceStore.selectForecastDateAvailable
            .toJSDate()
            .toISOString()
        : undefined;

    oceanWeatherIntelligenceStore.getGanttChartDataApi({
      operationRuleId: operationRuleId,
      cancelToken: source.token,
      create_at: selectedDate,
    });

    oceanWeatherIntelligenceStore.setChangeForUpdate({
      update: false,
      expiration: DateTime.local().plus({ minutes: 10 }).toMillis(),
    });

    return () => {
      source.cancel("Request cancelled");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    operationRuleStore.operationRuleMainId,
    oceanWeatherIntelligenceStore.actionCallGanttChart,
  ]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .custom-select .ant-select-selector {
        border: 1px solid!important;
        border-color: inherit !important;
        box-shadow: none !important; 
      }

      .custom-select .ant-select-selection-item {
        color: #187047 !important;
        font-size: 1rem;
        border-radius: 8px;
      }

      .highcharts-tooltip-container .gantt-tooltip-wrapper {
        min-width: 300px !important;
        max-width: 380px !important;
        white-space: normal !important;
      }

      .highcharts-tooltip-container .gantt-tooltip-wrapper > span {
        white-space: normal !important;
        min-width: 300px !important;
        max-width: 380px !important;
        display: block !important;
      }

      .highcharts-tooltip {
        min-width: 300px !important;
        max-width: 380px !important;
        white-space: normal !important;
      }

      .highcharts-tooltip > span {
        white-space: normal !important;
        min-width: 300px !important;
        max-width: 380px !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      oceanWeatherIntelligenceStore.setChangeForUpdate({
        update: true,
        expiration: 0,
      });
      oceanWeatherIntelligenceStore.setActionCallGanttChart();
    }, 15 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const chartRef = useRef<any>(null);

  // Modal state for creating dashboard
  const {
    isOpen: isCreateDashboardOpen,
    onOpen: onCreateDashboardOpen,
    onClose: onCreateDashboardClose,
  } = useDisclosure();

  // State for pin loading
  const [pinLoadingId, setPinLoadingId] = useState<number | null>(null);

  // Handle pin toggle for dashboard
  const handlePinToggle = useCallback(
    async (item: { id: number; isFavorite?: boolean }) => {
      setPinLoadingId(item.id);
      try {
        await operationRuleStore.addOperationRuleFavorite(item.id);
      } catch (error) {
        console.error("Error toggling favorite:", error);
      } finally {
        setPinLoadingId(null);
      }
    },
    [operationRuleStore],
  );

  const handleSelectDashboardChange = useCallback(
    (dashboardId: number) => {
      oceanWeatherIntelligenceStore.setChangeForUpdate({
        update: true,
        expiration: 0,
      });
      operationRuleLocationInsightStore.setOperationRuleLocationItens([]);
      operationRuleLocationStore.setOperationRuleLocations([]);
      operationRuleStore.setOperationRuleMain(dashboardId);
      operationRuleLocationStore.setHasLocations(true);
      operationRuleLocationInsightStore.setHasInsights(true);
    },
    [
      oceanWeatherIntelligenceStore,
      operationRuleLocationInsightStore,
      operationRuleLocationStore,
      operationRuleStore,
    ],
  );

  // Handle dashboard creation from modal
  const handleDashboardCreated = useCallback(
    (dashboardId: number) => {
      onCreateDashboardClose();
      handleSelectDashboardChange(dashboardId);
      oceanWeatherIntelligenceStore.setActionCallGanttChart();
    },
    [
      onCreateDashboardClose,
      handleSelectDashboardChange,
      oceanWeatherIntelligenceStore,
    ],
  );

  const handleGoBackCurrentInsight = useCallback(() => {
    oceanWeatherIntelligenceStore.clearSelectForecastDateAvailable();
    oceanWeatherIntelligenceStore.setActionCallGanttChart();
    setClickedDate(false);
    setStartDate(
      oceanWeatherIntelligenceStore.processDateCurrent(DateTime.local()),
    );
    oceanWeatherIntelligenceStore.setChangeForUpdate({
      update: true,
      expiration: 0,
    });
  }, [oceanWeatherIntelligenceStore]);

  // Handle toggle for DatePicker
  const handleDatePickerButtonClick = useCallback(
    (originalOnClick: () => void) => {
      if (isDatePickerOpen) {
        setIsDatePickerOpen(false);
      } else {
        originalOnClick();
      }
    },
    [isDatePickerOpen],
  );

  //@ts-ignore
  const CustomDatePickerButton = React.forwardRef(({ value, onClick }, ref) => (
    <Button
      //@ts-ignore
      ref={ref}
      className="CustomDatePickerButton"
      onClick={() => handleDatePickerButtonClick(onClick)}
      size="small"
      fontWeight={"light"}
      p={2}
      pr={4}
      layerStyle="lightOcean"
      _hover={{ layerStyle: "lightOcean" }}
      leftIcon={<TodayIcon style={{ marginLeft: "6px" }} />}
    >
      {clickedDate ? value : t("clickGoBackPastInsights")}
    </Button>
  ));

  const handleSelectForecastDateAvailable = useCallback(
    (date: Date) => {
      const timezoneOffset = DateTime.now().setZone(
        userConfigStore.timezone,
      ).offset;
      const timezoneOffsetLocal = DateTime.now().offset;
      const diff = timezoneOffset - timezoneOffsetLocal;

      const selectedDate = DateTime.fromISO(date.toISOString()).minus({
        minutes: diff,
      });

      oceanWeatherIntelligenceStore.setSelectForecastDateAvailable(
        selectedDate,
      );
      setStartDate(date);
      setClickedDate(true);
      const selectedDashboard = operationRuleStore.operationRuleMainId;

      if (!selectedDashboard || selectedDashboard <= 0) {
        message.info(t("needSelectDashboardPastInsight"));
      } else {
        oceanWeatherIntelligenceStore.setActionCallGanttChart();
        oceanWeatherIntelligenceStore.setChangeForUpdate({
          update: true,
          expiration: 0,
        });
      }
    },
    [oceanWeatherIntelligenceStore, operationRuleStore.operationRuleMainId, t],
  );

  const handleNavigateToEventManager = useCallback(() => {
    if (isEmbeddedMode()) {
      const dashboardId = operationRuleStore.operationRuleMainId;
      const path =
        dashboardId && dashboardId > 0
          ? `/event-manager?dashboard=${dashboardId}`
          : "/event-manager";
      navigateParent(path);
      return;
    }
    history.push("/insight-manager");
  }, [history, operationRuleStore.operationRuleMainId]);

  // Empty-state "Criar evento": event creation now lives in aurora-ui. When
  // embedded, ask the parent to open the event-manager for the selected
  // dashboard AND auto-open the create drawer (via `action=create`), mirroring
  // the event-manager's own "Novo evento" button. Falls back to the legacy
  // page only when running standalone (not embedded).
  const handleCreateEventInManager = useCallback(() => {
    if (isEmbeddedMode()) {
      const dashboardId = operationRuleStore.operationRuleMainId;
      const path =
        dashboardId && dashboardId > 0
          ? `/event-manager?dashboard=${dashboardId}&action=create`
          : "/event-manager?action=create";
      navigateParent(path);
      return;
    }
    history.push("/insight-manager?open_create_drawer=true");
  }, [history, operationRuleStore.operationRuleMainId]);

  // const handleRate = useCallback(
  //   (value: number) => {
  //     localStorage.setItem("showProbabilisticFeedback_show", "false");
  //     localStorage.setItem("showProbabilisticFeedback_answered", "true");

  //     oceanWeatherIntelligenceStore.addProductAnalyticsEvent(
  //       ProductAnalyticsEvents.InsightChartProbabilisticFeedback,
  //       {
  //         question: FeedbackQuestions.HOW_USEFUL_PROBABILITY_FEATURE,
  //         answer: value,
  //       }
  //     );
  //   },
  //   [oceanWeatherIntelligenceStore]
  // );

  // const handleProbabilityToggle = useCallback(
  //   (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const isActive = e.target.checked;

  //     if (isActive) {
  //       oceanWeatherIntelligenceStore.addProductAnalyticsEvent(
  //         ProductAnalyticsEvents.InsightChartProbabilisticOn,
  //         {}
  //       );
  //     } else {
  //       oceanWeatherIntelligenceStore.addProductAnalyticsEvent(
  //         ProductAnalyticsEvents.InsightChartProbabilisticOff,
  //         {}
  //       );
  //     }

  //     oceanWeatherIntelligenceStore.setIsSwitchProbabilityClicked(true);
  //     oceanWeatherIntelligenceStore.setIsGanttProbability(isActive);
  //     oceanWeatherIntelligenceStore.setActionCallGanttChart();
  //     oceanWeatherIntelligenceStore.setChangeForUpdate({
  //       update: true,
  //       expiration: 0,
  //     });

  //     const hasUserAnswered =
  //       localStorage.getItem("showProbabilisticFeedback_answered") === "true";

  //     if (!hasUserAnswered) {
  //       localStorage.setItem("showProbabilisticFeedback_show", "true");
  //       setTimeout(() => {
  //         oceanWeatherIntelligenceStore.setShowProbabilisticFeedback(true);
  //       }, 3000);
  //     }
  //   },
  //   [oceanWeatherIntelligenceStore]
  // );

  // const showToast = useCallback(() => {
  //   if (toast.isActive(toastId)) return;

  //   toast({
  //     id: toastId,
  //     position: "bottom-right",
  //     duration: null,
  //     isClosable: true,
  //     render: ({ onClose }) => {
  //       return (
  //         <FeedbackToast
  //           onRate={handleRate}
  //           onClose={onClose}
  //           scaleMax={5}
  //           title={t("leaveYourFeedback") as string}
  //           question={
  //             i18n.language === "en"
  //               ? FeedbackQuestions.HOW_USEFUL_PROBABILITY_FEATURE_EN
  //               : FeedbackQuestions.HOW_USEFUL_PROBABILITY_FEATURE
  //           }
  //           minLabel={t("notUsefulAtAll") as string}
  //           maxLabel={t("veryUseful") as string}
  //           thanksMessage={t("thanksForFeedback") as string}
  //           closeButtonMessage={t("close") as string}
  //         />
  //       );
  //     },
  //   });

  //   return toastId;
  // }, [toast]);

  // useFeedbackControl({
  //   autoShowKey: "showProbabilisticFeedback",
  //   onShow: showToast,
  //   trigger: oceanWeatherIntelligenceStore.showProbabilisticFeedback,
  // });

  const ganttOptions = useMemo(
    () => ({
      chart: {
        backgroundColor: "rgb(240, 241, 242)",
        className: "custom-gantt-chart",
        events: {
          load() {
            const chart: any = this;
            chartRef.current = chart;

            chart.plotBandsTooltip = [];

            setTimeout(() => {
              if (!chart.xAxis || !chart.xAxis[0]) return;

              chart.xAxis[0].plotLinesAndBands?.forEach(
                (plotBand: any, i: number) => {
                  if (plotBand.options.tooltipText && plotBand.svgElem) {
                    Highcharts.addEvent(
                      plotBand.svgElem.element,
                      "mousemove",
                      (e: any) => {
                        const tooltipText = plotBand.options.tooltipText;
                        const x =
                          e.pageX -
                          chart.container.getBoundingClientRect().left +
                          window.scrollX +
                          10;
                        const y =
                          e.pageY -
                          chart.container.getBoundingClientRect().top +
                          window.scrollY +
                          10;

                        if (!chart.plotBandsTooltip[i]) {
                          chart.plotBandsTooltip[i] = chart.renderer
                            .label(tooltipText, x, y, "callout")
                            .attr({
                              fill: "rgba(0, 0, 0, 0.75)",
                              zIndex: 10,
                              padding: 5,
                              r: 5,
                            })
                            .css({
                              color: "#ffffff",
                              transition: "opacity 0.5s",
                            })
                            .add();
                        } else {
                          chart.plotBandsTooltip[i]
                            .attr({ x, y })
                            .css({ opacity: 1 });
                        }
                      },
                    );

                    Highcharts.addEvent(
                      plotBand.svgElem.element,
                      "mouseout",
                      () => {
                        if (chart.plotBandsTooltip[i]) {
                          chart.plotBandsTooltip[i].css({ opacity: 0 });
                        }
                      },
                    );
                  }
                },
              );
            }, 500);
          },
        },
      },
      time: {
        timezoneOffset: oceanWeatherIntelligenceStore.timezoneOffset,
      },
      title: { text: "" },
      xAxis: {
        gridLineWidth: 1,
        ...ganttXAxisRange(
          oceanWeatherIntelligenceStore.ganttChartData[0]?.start,
          oceanWeatherIntelligenceStore.ganttChartData[0]?.end,
          ganttAxisAnchor(
            oceanWeatherIntelligenceStore.selectForecastDateAvailable,
            userConfigStore.timezone,
            today,
          ),
        ),
        currentDateIndicator: true,
        tickInterval: 3600 * 1000 * 6,
      },
      tooltip: {
        useHTML: true,
        backgroundColor: "#ffff",
        borderRadius: 8,
        outside: true,
        className: "gantt-tooltip-wrapper",
        style: {
          width: "380px",
          minWidth: "300px",
          maxWidth: "380px",
          whiteSpace: "normal",
        },
        formatter: function () {
          //@ts-ignore
          if (this.point?.isNull) {
            return "Null";
          }
          return oceanWeatherIntelligenceStore.ganttChartFormatTooltip(this);
        },
      },
      yAxis: {
        uniqueNames: true,
        type: "treegrid",
        grid: {
          columns: [
            {
              title: {
                text: i18n.t("oceanWeatherIntelligence.locationInsight"),
              },
              labels: {
                enabled: true,
                useHTML: true,
                //@ts-ignore
                formatter: function (
                  this: Highcharts.AxisLabelsFormatterContextObject,
                ) {
                  const name = this.value;

                  const ganttData =
                    oceanWeatherIntelligenceStore.ganttChartData[0]?.data;
                  // By id, not by name: a location row and the event under it
                  // may share a name (aurora only prefixes "Localidade - " when
                  // the event has no saved location of its own), and matching on
                  // the name then hands the event row the location's icon and
                  // swallows its status badge. The treegrid tick knows which row it is drawing, and
                  // its node carries that row's own id.
                  const gridNode = (this.axis as any)?.treeGrid
                    ?.mapOfPosToGridNode?.[this.pos];
                  const currentItem =
                    (gridNode?.id &&
                      ganttData?.find((item) => item.id === gridNode.id)) ||
                    ganttData?.find((item) => item.name === name);

                  const statusText = rowStatusText(currentItem, (key, options) =>
                    options ? i18n.t(key, options) : i18n.t(key),
                  );

                  // `locationType` only ever lands on a location row, but the
                  // name fallback above can still resolve to one from an event
                  // row when the two collide.
                  const isLocationRow = currentItem?.isLocation === true;

                  if (isLocationRow && currentItem?.locationType === "point") {
                    const locationIcon = `<img src="/locate.png" alt="Location Icon" title="${i18n.t(
                      "oceanWeatherIntelligence.point",
                    )}" width="18" height="18" style="margin-right: 4px; vertical-align: middle; flex-shrink: 0;" />`;
                    return `<div style="display: flex; align-items: center;">${locationIcon}<span style="line-height: 1.4;">${name}${statusText}</span></div>`;
                  } else if (
                    isLocationRow &&
                    currentItem?.locationType === "area"
                  ) {
                    const vectorSquareIcon = `<img src="/vector-square.png" alt="Vector Square Icon" title="${i18n.t(
                      "oceanWeatherIntelligence.area",
                    )}" width="16" height="16" style="margin-right: 4px; vertical-align: middle; flex-shrink: 0;" />`;
                    return `<div style="display: flex; align-items: center;">${vectorSquareIcon}<span style="line-height: 1.4;">${name}${statusText}</span></div>`;
                  }

                  return `<div style="display: flex; align-items: center;"><span style="line-height: 1.4;">${name}${statusText}</span></div>`;
                },
              },
            },
          ],
        },
        events: {
          afterExpand: function (event: any) {
            // oceanWeatherIntelligenceStore.addProductAnalyticsEvent(
            //   ProductAnalyticsEvents.InsightChartProbabilistic,
            //   { insight_name: event.name }
            // );
          },
        },
      },
      series: oceanWeatherIntelligenceStore.ganttChartData,
    }),
    // `operationRules` is deliberately NOT a dependency: nothing in this memo
    // reads it, and it is rebuilt by identity on every favourite toggle
    // (updateFavorite maps the list into new objects). HighchartsReact runs with
    // `immutable`, so a new `options` reference DESTROYS and recreates the
    // chart - which made the gantt visibly blink when the user only pinned a
    // dashboard. Switching dashboards still rebuilds it, via ganttChartData and
    // the `gantt-${operationRuleMainId}` key on the element.
    //
    // The selected past date and the user's zone ARE dependencies: an empty Gantt
    // opens on that day (`ganttAxisAnchor`, aurora#1619). Both change only on a user
    // action that refetches anyway, so they cost no extra rebuild of a live chart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      oceanWeatherIntelligenceStore.ganttChartData,
      oceanWeatherIntelligenceStore.state,
      oceanWeatherIntelligenceStore.selectForecastDateAvailable,
      userConfigStore.timezone,
    ],
  );

  const isInsightEmpty = isGanttWithoutEvents(
    oceanWeatherIntelligenceStore.ganttChartData?.[0]?.data,
    oceanWeatherIntelligenceStore.ganttChartData?.[0]?.end,
    oceanWeatherIntelligenceStore.state === StoreState.DONE,
  );

  const now = useMemo(() => DateTime.local().toJSDate(), []);

  return (
    <Box mt={8} mb={8} mx={SCREEN_HORIZONTAL_SPACING}>
      <Flex justifyContent="space-between" alignItems="center">
        <PageTitle text={t("operationInsights")} />
      </Flex>

      {operationRuleStore.state === StoreState.ERROR && (
        <Alert status="error" mb={4} color="black">
          <AlertIcon />
          {oceanWeatherIntelligenceStore.messageError}
        </Alert>
      )}

      {!operationRuleStore.operationRules?.length &&
        !operationRuleStore.hasDashboards && (
          <EmptyState
            description={t("weStillDontHaveAnyDashboard")}
            image={noDashboards}
            imageSize="25vw"
            buttonAction={handleNavigateToEventManager}
            buttonIcon={<PlayArrowIcon />}
            buttonText={
              !authStore.readOnlyUser ? (t("letsGo") as string) : undefined
            }
            marginTop="10vh"
          />
        )}

      {/* <SeverityTutorialModal
        isOpen={isSeverityTutorialOpen}
        onClose={onSeverityTutorialClose}
      /> */}

      {operationRuleStore.operationRules?.length > 0 && (
        <>
          <Flex flexWrap="wrap" gap={6} align="center" mt={4}>
            {/* Left section: Dashboard selector + "+" button + separator */}
            <Flex align="center" gap={6}>
              <Flex align="center" gap={4}>
                <Flex direction="column" minW="250px">
                  <DashboardSelect
                    operationRules={operationRuleStore.operationRules ?? []}
                    selectedId={
                      operationRuleStore.operationRuleMainId > 0
                        ? operationRuleStore.operationRuleMainId
                        : undefined
                    }
                    onChange={(id) => handleSelectDashboardChange(id)}
                    onPinToggle={handlePinToggle}
                    showActions={false}
                    readOnly={authStore.readOnlyUser}
                    pinLoadingId={pinLoadingId}
                    hideIcon={true}
                  />
                </Flex>

                {/* "+" button to create new dashboard */}
                {!authStore.readOnlyUser && (
                  <IconButton
                    aria-label={tEvent("createDashboard")}
                    icon={<Icon as={Plus} boxSize={5} />}
                    layerStyle="lightOcean"
                    _hover={{ layerStyle: "lightOcean" }}
                    borderRadius="6px"
                    size="md"
                    minW="36px"
                    h="36px"
                    onClick={onCreateDashboardOpen}
                  />
                )}
              </Flex>

              {/* Vertical separator */}
              <Box h="50px" w="1px" bg="gray.400" />
            </Flex>

            {/* Right section: History DatePicker and Manager buttons */}
            <Flex align="center" gap={4}>
              {clickedDate && (
                <Button
                  size="small"
                  p={2}
                  pr={4}
                  fontWeight={"light"}
                  layerStyle="lightOcean"
                  _hover={{ layerStyle: "lightOcean" }}
                  leftIcon={<ArrowBackIcon />}
                  onClick={handleGoBackCurrentInsight}
                >
                  {t("currentInsight")}
                </Button>
              )}

              <Box position="relative">
                <DatePicker
                  selected={startDate}
                  onChange={handleSelectForecastDateAvailable}
                  maxDate={now}
                  timeIntervals={15}
                  popperPlacement={"left-start"}
                  customInput={<CustomDatePickerButton />}
                  dateFormat="MMMM d, yyyy H:mm"
                  timeInputLabel={t("time") as string}
                  showTimeInput
                  locale={getDateFnsLocale(i18n.language)}
                  open={isDatePickerOpen}
                  onCalendarOpen={() => setIsDatePickerOpen(true)}
                  onCalendarClose={() => setIsDatePickerOpen(false)}
                />
              </Box>

              <Button
                leftIcon={<Icon as={ChartBarStacked} boxSize={4} />}
                bg="#286795"
                color="white"
                _hover={{ bg: "#0773b3" }}
                _active={{ bg: "#066399" }}
                size="md"
                h="36px"
                borderRadius="6px"
                fontWeight="medium"
                px={5}
                onClick={handleNavigateToEventManager}
              >
                {t("eventManager")}
              </Button>
            </Flex>
          </Flex>

          {/* Commented probability controls */}
          {/* <Flex
              align="center"
              justifyContent={"end"}
              ml="auto"
              minW="200px"
              gap="16px"
            >
              <Flex alignItems="center" gap="4px">
                <BadgePercent
                  size="20px"
                  color="#086AA9"
                  style={{ marginBottom: "3px" }}
                />
                <Text color="#086AA9">{t("probabilitySwitch")}</Text>
              </Flex>
              <Flex alignItems={"center"} gap="8px">
                <Switch
                  p={0}
                  colorScheme="facebook"
                  size="md"
                  mb={"3px"}
                  id="show-insight-chart"
                  isChecked={oceanWeatherIntelligenceStore.isGanttProbability}
                  onChange={handleProbabilityToggle}
                />
                <Tooltip
                  label={t("whenYouDisable") as string}
                  fontSize="sm"
                  hasArrow
                  placement="left"
                >
                  <InfoIcon
                    size="16px"
                    color="black"
                    style={{ marginBottom: "3px" }}
                  />
                </Tooltip>
              </Flex>
            </Flex> */}

          {oceanWeatherIntelligenceStore.ganttChartData?.length > 0 &&
            oceanWeatherIntelligenceStore.ganttChartData[0].data?.some(
              (item) => item?.error_message,
            ) && (
              <Alert status="error" mt={2} color="black">
                <AlertIcon />
                <Text>{t("thereAreInsightsWithErrors")}</Text>
              </Alert>
            )}

          {isInsightEmpty && !authStore.readOnlyUser && (
            <Alert color="gray.600" status="warning" mt={2}>
              <AlertIcon />
              <Text mr={1}>{t("almostThereCreateInsightVisualizeChart")}</Text>
              <Text
                cursor="pointer"
                color="#dd6b20"
                _hover={{ color: "rgba(221,107,32,0.7)" }}
                onClick={handleNavigateToEventManager}
              >
                {t("clickHere")}
              </Text>
            </Alert>
          )}

          {/* {showRiskAlert && (
            <Alert
              color="gray.600"
              colorScheme={"purple"}
              mt={4}
              mb={2}
              gap={2}
            >
              <Info size={20} style={{ marginBottom: "2px" }} color="#641BA3" />
              <Flex color="#641BA3">
                <Text mr={1}>{t("checkTheRiskLevels")}</Text>
                <Text
                  textDecoration={"underline"}
                  cursor="pointer"
                  onClick={() => {
                    onSeverityTutorialOpen();
                    oceanWeatherIntelligenceStore.addProductAnalyticsEvent(
                      ProductAnalyticsEvents.InsightChartProbabilisticTutorialOpen,
                      {}
                    );
                  }}
                >
                  {t("clickToLearnMore")}
                </Text>
              </Flex>
              <CloseButton
                position="absolute"
                right="8px"
                top="8px"
                onClick={() => {
                  setShowRiskAlert(false);
                  localStorage.setItem(
                    "newsAlertProbabilisticIRisknsight",
                    "true"
                  );
                }}
              />
            </Alert>
          )} */}

          {oceanWeatherIntelligenceStore.state === StoreState.PEDDING && (
            <Center h={"50vh"}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Center>
          )}

          {oceanWeatherIntelligenceStore.ganttChartData?.length > 0 &&
            !oceanWeatherIntelligenceStore.ganttChartData[0].data.length &&
            oceanWeatherIntelligenceStore.state === StoreState.DONE && (
              <EmptyState
                key={`dash-${operationRuleStore.operationRuleMainId}`}
                title={t("dashboardNoEventsTitle") as string}
                description={t("dashboardNoEventsDescription")}
                image={dashboardHasNoLocation}
                imageSize="20vw"
                buttonAction={handleCreateEventInManager}
                buttonIcon={<Plus size={18} />}
                buttonText={
                  !authStore.readOnlyUser
                    ? (tEvent("createEvent") as string)
                    : undefined
                }
                marginTop="10vh"
              />
            )}

          {oceanWeatherIntelligenceStore.ganttChartData?.length > 0 &&
            oceanWeatherIntelligenceStore.ganttChartData[0].data.length &&
            oceanWeatherIntelligenceStore.state === StoreState.DONE &&
            operationRuleStore.operationRules?.length > 0 && (
              <GanttChart
                key={`gantt-${operationRuleStore.operationRuleMainId}`}
                style={{ maxWidth: windowSize.width }}
                timezoneOffset={oceanWeatherIntelligenceStore.timezoneOffset}
                options={ganttOptions}
              />
            )}
        </>
      )}

      {/* Modal for creating new dashboard */}
      <CreateDashboardModal
        isOpen={isCreateDashboardOpen}
        onClose={onCreateDashboardClose}
        onDashboardCreated={handleDashboardCreated}
        t={tEvent}
      />
    </Box>
  );
};

export default observer(OceanWeatherIntelligence);
